import * as React from "react";
import * as ReactDOM from "react-dom";

import {
  DirectionalHint,
  elementContains,
  KeyCodes,
  WindowProvider,
} from "@fluentui/react";

import {
  FunctionOrValue,
  getHoverableTooltipsPreference,
  ribbonElementTextContainerSelector,
  SetTooltipProps,
  shyComponentChangeEventName,
} from "@1js/acui-common";
import { AppContext, makeAppContextValue } from "@1js/acui-context";

import { AppTooltip } from "../AppTooltip";
import { AppTooltipManagerProps } from "./AppTooltipManager.Props";

export class AppTooltipManager extends React.PureComponent<
  AppTooltipManagerProps,
  {}
> {
  /* setup AppContext to get access to the current appWindow object */
  static contextType = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  /* The DOM element that the tooltip will mount to */
  private tooltipWrapper = React.createRef<HTMLDivElement>();

  /* The DOM element that the tooltip's callout will target */
  private tooltipElement: HTMLElement | undefined;

  /* A unique identifier for the tooltip */
  private tooltipId: string | undefined;

  /* The text or function returning the text displayed on the tooltip */
  private tooltipLabel: FunctionOrValue<string> | undefined;

  /* Margin applied to the top of the tooltip. This is necessary for preview gallery
    buttons, which sit a few pixels above all other controls in the bottom bar. */
  private tooltipTopMargin: number | undefined;

  /* True if top level tooltips should be hidden. This occurs when a menu is dropped in the bottom bar */
  private hideTopLevelTooltips: boolean = false;

  /* True if the queued tooltipElement is in a menu. Menu-based tooltips should still render when hideTopLevelTooltips is true */
  private isMenuComponent: boolean | undefined;

  /* Optional direction for the tooltip; default is BottomCenter */
  private tooltipDirection: DirectionalHint | undefined;

  /* Optional direction for the tooltip; default is undefined */
  private tooltipDirectionForRTL: DirectionalHint | undefined;

  /* The callback passed in to dismiss the tooltip */
  private dismissTooltipCallback?: () => void;

  /* True if the next tooltip should render with a delay */
  private delayTooltip: boolean = true;

  /* Tracks the ID of the timeouts so that they can be cleared by another event */
  private timeoutID: number = 0;

  /* True if there is currently a tooltip on the screen. This accounts for the animation delay after a tooltip is mounted */
  private tooltipIsVisible: boolean = false;

  /* Tracks the ID of the hover timeouts so that they can be cleared by another event */
  private hoverTimeout: number = 0;

  /* Used for tracking if mouse has re-entered tooltip after getting a mouseLeave event */
  private mouseEntered: boolean = false;

  /* True if the hover timeout is what is causing the tooltip to dismiss */
  private dismissFromTimeout: boolean = false;

  public render() {
    return (
      <AppContext.Consumer>
        {(context) => (
          <AppContext.Provider
            value={makeAppContextValue(
              context.isExperimentalFeatureEnabled,
              this.setTooltipElement,
              this.updateTooltipsHidden,
              context.appWindow
            )}
          >
            {this.props.children}
            <div ref={this.tooltipWrapper} />
          </AppContext.Provider>
        )}
      </AppContext.Consumer>
    );
  }

  public componentDidMount() {
    // register for keydown events so we can dismiss tooltips on a shortcut key
    document.addEventListener("keydown", this.globalKeyDown, { capture: true });
    document.addEventListener(
      shyComponentChangeEventName,
      this.onShyComponentChange
    );
  }

  public componentWillUnmount() {
    document.removeEventListener("keydown", this.globalKeyDown, {
      capture: true,
    });
    document.removeEventListener(
      shyComponentChangeEventName,
      this.onShyComponentChange
    );
  }

  private getAppToolTip() {
    const hoverablePreference: boolean = getHoverableTooltipsPreference();
    return (
      <WindowProvider window={this.context.appWindow}>
        <AppTooltip
          appColors={this.props.appColors}
          targetElement={this.tooltipElement}
          content={this.tooltipLabel}
          id={this.tooltipId}
          topMargin={this.tooltipTopMargin}
          delayTooltip={this.delayTooltip}
          hidden={!this.tooltipElement || !this.tooltipLabel || !this.tooltipId}
          directionalHint={this.tooltipDirection}
          directionalHintForRTL={this.tooltipDirectionForRTL}
          mouseLeaveCallback={
            hoverablePreference ? this.onMouseLeave : undefined
          }
          mouseEnterCallback={
            hoverablePreference ? this.onMouseEnter : undefined
          }
        />
      </WindowProvider>
    );
  }

  /**
   * Tooltips are rendered from an instance method so that we don't unnecessarily trigger renders of the ribbon
   */
  private renderTooltip = () => {
    this.tooltipIsVisible =
      !this.delayTooltip &&
      !!this.tooltipElement &&
      !!this.tooltipLabel &&
      !!this.tooltipId;
    this.delayTooltip && this.overwriteTooltip(); // To show the delay/animation, the tooltip must first be remounted
    if (
      this.tooltipWrapper.current &&
      (!this.hideTopLevelTooltips || this.isMenuComponent)
    ) {
      ReactDOM.render(
        getHoverableTooltipsPreference()
          ? ReactDOM.createPortal(
              this.getAppToolTip(),
              this.tooltipWrapper.current!
            )
          : this.getAppToolTip(),
        this.tooltipWrapper.current
      );
    } else {
      this.overwriteTooltip();
    }
  };

  /**
   * If a tooltip is not set, an empty div is rendered in it's place
   */
  private overwriteTooltip = () => {
    if (this.tooltipWrapper.current) {
      ReactDOM.render(<div />, this.tooltipWrapper.current);
    }
  };

  private clearTimeoutIfNeeded = (
    currentTooltipId?: string,
    newTooltipId?: string
  ): void => {
    // Clear the current tooltip timeout and dismissal info if:
    if (
      // the callback to set the tooltip is coming from the expired timeout
      this.dismissFromTimeout ||
      // or there timeout has expired and a new tooltip is showing/hiding
      ((this.hoverTimeout !== 0 || this.hoverTimeout !== undefined) &&
        currentTooltipId !== newTooltipId)
    ) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = 0;
      this.dismissFromTimeout = false;
    }
  };

  /**
   * This is the callback that each component uses to set a tooltip via the AppTooltipHost.
   * @param {HTMLElement} element - The DOM element that we'll anchor the tooltip to
   * @param {string} label - The string that will be displayed in the tooltip
   * @param {string} id - The id of the component that is hosting the tooltip
   * @param {number} tooltipTopMargin - Margin added to the top of the tooltip
   * @param {boolean} isMenuComponent - True if the element is inside a menu
   */
  private setTooltipElement = (tooltipProps: SetTooltipProps): void => {
    if (tooltipProps.element) {
      const textContainer = tooltipProps.element.querySelector(
        ribbonElementTextContainerSelector
      );
      const tooltipLabelValue =
        typeof tooltipProps.label === "function"
          ? tooltipProps.label()
          : tooltipProps.label;

      // If the label (e.g. textContainer) is the same as the tooltip value and is displayed, we need to not show the tooltip
      if (
        textContainer &&
        textContainer.textContent === tooltipLabelValue &&
        window.getComputedStyle(textContainer).display !== "none"
      ) {
        return;
      }
    }

    this.clearTimeoutIfNeeded(this.tooltipId, tooltipProps.id);
    if (tooltipProps.element) {
      tooltipProps.element.addEventListener(
        "mouseleave",
        this.onMouseLeave,
        false
      );
      tooltipProps.element.addEventListener(
        "mouseenter",
        this.onMouseEnter,
        false
      );
    } else if (this.tooltipElement) {
      this.tooltipElement.removeEventListener(
        "mouseleave",
        this.onMouseLeave,
        false
      );
      this.tooltipElement.removeEventListener(
        "mouseenter",
        this.onMouseEnter,
        false
      );
    }

    this.tooltipElement = tooltipProps.element;
    this.tooltipLabel = tooltipProps.label;
    this.tooltipId = tooltipProps.id;
    this.tooltipTopMargin = tooltipProps.tooltipTopMargin;
    this.isMenuComponent = tooltipProps.isMenuComponent;
    this.tooltipDirection = tooltipProps.tooltipDirection;
    this.tooltipDirectionForRTL = tooltipProps.tooltipDirectionForRTL;
    this.dismissTooltipCallback = tooltipProps.dismissTooltipCallback;

    this.setDelayTimeout();
    this.trackTooltipVisibility();
    this.renderTooltip();
  };

  /**
   * This callback is used when top level tooltips should be hidden. This is currently set to true when a menu is dropped.
   */
  private updateTooltipsHidden = (hideTopLevelTooltips: boolean) => {
    this.hideTopLevelTooltips = hideTopLevelTooltips;
    this.tooltipIsVisible = false;
    this.renderTooltip();
  };

  /**
   * If a tooltip is trigged within 500ms of a tooltip being show, we want to render the tooltip without a delay.
   */
  private setDelayTimeout = () => {
    if (this.tooltipIsVisible && !this.tooltipElement) {
      this.delayTooltip = false;
      clearTimeout(this.timeoutID);
      this.timeoutID = window.setTimeout(() => {
        this.delayTooltip = true;
        this.timeoutID = 0;
      }, 500);
    }
    this.tooltipIsVisible = this.tooltipElement ? this.tooltipIsVisible : false;
  };

  /**
   * The tooltip initially renders with a 500ms delay. This timout is used to track whether the tooltip is visible
   * on the screen. This is necessary in determining the delay for future tooltip renders.
   */
  private trackTooltipVisibility = () => {
    if (!this.tooltipIsVisible && this.tooltipElement) {
      clearTimeout(this.timeoutID);
      this.timeoutID = window.setTimeout(() => {
        this.tooltipIsVisible = true;
      }, 500);
    }
  };

  /**
   * If the user hits ctrl when a tooltip is open, close it
   */
  private globalKeyDown = (ev: KeyboardEvent): void => {
    if (
      this.tooltipIsVisible &&
      this.tooltipElement &&
      ev.ctrlKey &&
      !ev.shiftKey &&
      ev.which === KeyCodes.ctrl
    ) {
      ev.stopPropagation();
      this.dismissTooltipCallback && this.dismissTooltipCallback();
    }
  };

  /**
   * When a shy component changes state we should be dismissing tooltips
   */
  private onShyComponentChange = () => {
    if (
      this.tooltipIsVisible &&
      this.tooltipElement &&
      this.dismissTooltipCallback
    ) {
      this.dismissTooltipCallback();
    }
  };

  private isMouseWithinTooltipOrAnchor = (target: EventTarget): boolean => {
    // if the mouse did not go to the callout wrapper
    return (
      target === this.tooltipWrapper.current ||
      // and the mouse is not within the callout/tooltip
      elementContains(this.tooltipWrapper.current, target as HTMLElement) ||
      // and the mouse is not within the element
      target === this.tooltipElement ||
      // and the mouse is not within the element's children
      elementContains(this.tooltipElement as HTMLElement, target as HTMLElement)
    );
  };

  /**
   * Tracks the mouse events for the tooltip callout and the element
   * to not dismiss the tooltip when we hover over it
   */
  private onMouseLeave = (event: MouseEvent) => {
    if (!getHoverableTooltipsPreference()) {
      this.dismissTooltipCallback && this.dismissTooltipCallback();
      return;
    }

    if (
      event.relatedTarget &&
      !this.isMouseWithinTooltipOrAnchor(event.relatedTarget)
    ) {
      this.delayTooltip = false;
      this.mouseEntered = false;
      // if there is no current timeout tracking
      if (this.hoverTimeout === 0 || this.hoverTimeout === undefined) {
        // set timeout for 500ms to delay dismissal to make it easier for users to hover
        this.hoverTimeout = window.setTimeout(() => {
          if (!this.mouseEntered) {
            this.dismissFromTimeout = true;
            this.dismissTooltipCallback && this.dismissTooltipCallback();
          }
          this.hoverTimeout = 0;
        }, 500);
      }
    }
  };

  private onMouseEnter = (event: MouseEvent) => {
    if (!getHoverableTooltipsPreference()) {
      return;
    }

    // track if mouse has re-entered tooltip or anchor so we don't dismiss on timeout
    if (event.target && this.isMouseWithinTooltipOrAnchor(event.target)) {
      this.mouseEntered = true;
    }
  };
}
